/**
 * Report parameters live in the URL query string (SPEC §9.1).
 *
 * The requirement is one sentence and it drives every decision here: *a report
 * with its filters set must be shareable as a link*. So the encoding is
 * readable, stable, and lossless for every `ParamSpec.kind`:
 *
 * ```
 * ?window=2026-01-01..2026-06-30&department=finance&regions=East&regions=West
 * ```
 *
 * One query key per parameter, named after the parameter. A date range is a
 * single `from..to` key rather than two, so the URL cannot be half-edited into
 * a state the manifest has no name for. A multiselect repeats its key, which
 * is what a form would send and what a human would guess.
 *
 * **Defaults are materialised into the URL, not left implicit.** The report
 * view resolves every parameter and rewrites the address bar once on load. A
 * link therefore pins what the sender was actually looking at, even if the
 * manifest default changes next month — which is the whole point of being able
 * to send one.
 */

import type { Option, ParamSpec } from './types'

export interface DateRange {
  readonly from: string
  readonly to: string
}

export type ParamValue = string | number | boolean | readonly (string | number)[] | DateRange | null

export type ParamValues = Readonly<Record<string, ParamValue>>

/** Separates the two halves of a `date_range` in the query string. */
const RANGE_SEPARATOR = '..'

const isDateRange = (value: ParamValue): value is DateRange =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const isoDate = (date: Date): string => date.toISOString().slice(0, 10)

/**
 * A scalar as query-string text.
 *
 * Narrow on purpose. Anything that is not already a string, number, or boolean
 * has no single obvious spelling in a URL, and letting `String()` decide would
 * put `[object Object]` in a link somebody then shares.
 */
export function renderScalar(value: unknown): string | null {
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  return null
}

/**
 * The window a required `date_range` with no manifest default starts on.
 *
 * Something has to be chosen — the parameter is required, so an empty form
 * cannot run at all — and the choice is written straight into the URL, so it
 * is visible and shareable rather than a hidden assumption. A manifest may
 * size the window with `default_window_days`; otherwise it reaches 29 days
 * back (a 30-day window).
 */
export function defaultDateRange(now: Date = new Date(), days = 29): DateRange {
  const from = new Date(now)
  from.setDate(from.getDate() - days)
  return { from: isoDate(from), to: isoDate(now) }
}

/** The value a parameter starts on: its manifest default, or a usable stand-in. */
export function initialValue(spec: ParamSpec, now: Date = new Date()): ParamValue {
  const declared = spec.default

  if (spec.kind === 'date_range') {
    if (Array.isArray(declared) && declared.length === 2) {
      const from = renderScalar(declared[0])
      const to = renderScalar(declared[1])
      if (from !== null && to !== null) return { from, to }
    }
    if (declared !== null && typeof declared === 'object') {
      const range = declared as { from?: unknown; to?: unknown }
      const from = renderScalar(range.from)
      const to = renderScalar(range.to)
      if (from !== null && to !== null) return { from, to }
    }
    return defaultDateRange(now, spec.default_window_days ?? 29)
  }

  if (declared !== null && declared !== undefined) {
    if (spec.kind === 'multiselect') {
      return Array.isArray(declared)
        ? (declared as (string | number)[])
        : [declared as string | number]
    }
    return declared as ParamValue
  }

  switch (spec.kind) {
    case 'multiselect':
      return []
    case 'boolean':
      return false
    case 'date':
      return isoDate(now)
    default:
      // A required select or text with no default: empty, and the ParamBar
      // shows it as unset rather than inventing a value that means something.
      return null
  }
}

export function initialValues(specs: readonly ParamSpec[], now: Date = new Date()): ParamValues {
  return Object.fromEntries(specs.map((spec) => [spec.name, initialValue(spec, now)]))
}

/** One parameter's value, as it appears in the query string. */
function encode(spec: ParamSpec, value: ParamValue): string[] {
  if (value === null || value === '') return []
  if (spec.kind === 'date_range') {
    if (!isDateRange(value)) return []
    return [`${value.from}${RANGE_SEPARATOR}${value.to}`]
  }
  if (Array.isArray(value)) {
    // Repeated key. An empty multiselect writes no key at all, so "nothing
    // selected" and "parameter absent" are the same URL — which they are.
    return value.map(renderScalar).filter((item) => item !== null)
  }
  if (isDateRange(value)) return []
  const rendered = renderScalar(value)
  return rendered === null ? [] : [rendered]
}

/** Build the query string for a full set of values. */
export function toSearchParams(
  specs: readonly ParamSpec[],
  values: ParamValues,
): URLSearchParams {
  const search = new URLSearchParams()
  // Manifest order, not object order, so the same view always produces the
  // same URL — two people comparing links should see identical strings.
  for (const spec of specs) {
    for (const encoded of encode(spec, values[spec.name] ?? null)) {
      search.append(spec.name, encoded)
    }
  }
  return search
}

function decode(spec: ParamSpec, raw: string[]): ParamValue {
  switch (spec.kind) {
    case 'date_range': {
      const [from, to] = (raw[0] ?? '').split(RANGE_SEPARATOR)
      // A malformed range is discarded rather than half-applied; the caller
      // falls back to the default, and the rewrite puts a valid one back in
      // the URL. Binding half a range would silently change the numbers.
      if (from === undefined || to === undefined || from === '' || to === '') return null
      return { from, to }
    }
    case 'multiselect':
      return raw
    case 'number': {
      const parsed = Number(raw[0])
      return Number.isNaN(parsed) ? null : parsed
    }
    case 'boolean':
      return raw[0] === 'true'
    default:
      // select / date / text stay strings. A select's option values may be
      // integers server-side; `reconcileWithOptions` restores the real type
      // once the option list is known, because the URL cannot carry it.
      return raw[0] ?? null
  }
}

/**
 * Read values out of a query string, falling back to defaults for anything
 * absent or malformed.
 *
 * Unknown query keys are ignored rather than rejected: a URL may legitimately
 * carry unrelated tracking parameters, and refusing to render a report because
 * of one would be its own kind of wrong.
 */
export function fromSearchParams(
  specs: readonly ParamSpec[],
  search: URLSearchParams,
  now: Date = new Date(),
): ParamValues {
  const values: Record<string, ParamValue> = {}
  for (const spec of specs) {
    const raw = search.getAll(spec.name)
    const decoded = raw.length === 0 ? null : decode(spec, raw)
    values[spec.name] = decoded === null ? initialValue(spec, now) : decoded
  }
  return values
}

/**
 * Restore an option's real type once its list has loaded.
 *
 * A query string cannot distinguish the integer `7` from the string `"7"`, and
 * a dynamic `options_query` may return either. Matching on the rendered form
 * and adopting the option's own value keeps a shared link working against an
 * integer-keyed picker instead of quietly returning no rows.
 */
export function reconcileWithOptions(
  spec: ParamSpec,
  value: ParamValue,
  options: readonly Option[],
): ParamValue {
  if (spec.kind !== 'select' && spec.kind !== 'multiselect') return value
  const byRendered = new Map(options.map((option) => [String(option.value), option.value]))

  if (spec.kind === 'multiselect') {
    if (!Array.isArray(value)) return value
    // A selection the current options no longer contain is dropped: §9.4 says
    // a cascading change clears invalid selections, and a value the picker
    // cannot show is a filter the user cannot see they have applied.
    return value.map((item) => byRendered.get(String(item))).filter((item) => item !== undefined)
  }

  if (value === null || Array.isArray(value) || isDateRange(value)) return value
  return byRendered.get(String(value)) ?? null
}

/** The payload `POST /run` wants: `date_range` as `{from, to}`, the rest as-is. */
export function toRequestParams(
  specs: readonly ParamSpec[],
  values: ParamValues,
): Record<string, unknown> {
  const payload: Record<string, unknown> = {}
  for (const spec of specs) {
    const value = values[spec.name] ?? null
    // An unset optional parameter is omitted so the server fills its declared
    // default, rather than being sent an explicit null that means something
    // different to the coercer.
    if (value === null || value === '') continue
    if (spec.kind === 'multiselect' && Array.isArray(value) && value.length === 0) continue
    payload[spec.name] = value
  }
  return payload
}

/** The dependency values one cascading parameter's options query needs. */
export function dependencyValues(
  spec: ParamSpec,
  specs: readonly ParamSpec[],
  values: ParamValues,
): Record<string, unknown> {
  const needed = specs.filter((candidate) => spec.depends_on.includes(candidate.name))
  return toRequestParams(needed, values)
}

/** True when every parameter a report requires has a value to send. */
export function isRunnable(specs: readonly ParamSpec[], values: ParamValues): boolean {
  return specs.every((spec) => {
    if (!spec.required) return true
    const value = values[spec.name] ?? null
    if (value === null || value === '') return false
    if (Array.isArray(value) && value.length === 0) return false
    return true
  })
}
